import config from "@Config/index";
import { PAGE } from "constants/configConstants";
import getCookie from "helpers/getCookie";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import {
  OVERVIEW_GALLERY_AB_TEST,
  OVERVIEW_GALLERY_MIN_WIDTH,
} from "../hooks/useOverviewGalleryArm";

// Hero gallery experiment readout events; gtagEventEmitter fans out to GA and PostHog. The arm
// label is worked out here, once, at event time: call sites spread getOverviewGalleryEventProps()
// instead of deciding the label themselves.

export type GalleryVariant = "new" | "control";
export type HeroMediaButton = "photos" | "videos" | "3d" | "map" | "floor_plan";

/**
 * The arm the user is exposed to right now, or null. Exposed = desktop global build at ≥1200
 * (below it both arms see today's gallery, FR-18), with the CDN-assigned `overview_gallery`
 * cookie, and not an internal user (they always see the new gallery and are kept out of the
 * experiment; the server sets `is_internal_user` for them on desktop pages). Only called from
 * the property page. Matches useOverviewGalleryArm().arm, for code outside render.
 */
export const getOverviewGalleryArm = (): GalleryVariant | null => {
  if (
    typeof window === "undefined" ||
    !__DESKTOP__ ||
    config.IS_CHINA_APP ||
    config.IS_WHITELABEL_APP ||
    window.innerWidth < OVERVIEW_GALLERY_MIN_WIDTH ||
    getCookie("is_internal_user") === "true"
  ) {
    return null;
  }
  const value = getCookie(OVERVIEW_GALLERY_AB_TEST);
  return value === "new" || value === "control" ? value : null;
};

/**
 * `{ gallery_variant }` for an event, or `{}` when the user is not exposed: the property is
 * omitted, never sent empty, so events outside the experiment are unchanged. China desktop at
 * ≥1200 always sees the new gallery outside the experiment, and is labelled `"new"` so its own
 * PostHog project can tell the galleries apart; it never gets an arm or experiment_viewed.
 */
export const getOverviewGalleryEventProps = (): { gallery_variant?: GalleryVariant } => {
  if (
    config.IS_CHINA_APP &&
    typeof window !== "undefined" &&
    __DESKTOP__ &&
    window.innerWidth >= OVERVIEW_GALLERY_MIN_WIDTH
  ) {
    return { gallery_variant: "new" };
  }
  const arm = getOverviewGalleryArm();
  return arm ? { gallery_variant: arm } : {};
};

interface HeroImageClick {
  propertyId: string | number;
  tilePosition: number;
  tileType: string;
  layoutCase: string; // "1"…"5+", or "control"
  mediaCount: number;
  heroSlideIndex?: number; // control only
}

/** E-2. Sent only for exposed users (both arms), so callers needn't check the arm. */
export const trackHeroImageClick = ({
  propertyId,
  tilePosition,
  tileType,
  layoutCase,
  mediaCount,
  heroSlideIndex,
}: HeroImageClick) => {
  const experimentProps = getOverviewGalleryEventProps();
  if (!experimentProps.gallery_variant) return;
  gtagEventEmitter({
    action: "hero_gallery_image_clicked",
    category: PAGE.INVENTORY,
    label: tileType,
    ...experimentProps,
    property_id: propertyId,
    tile_position: tilePosition,
    tile_type: tileType,
    layout_case: layoutCase,
    media_count: mediaCount,
    ...(heroSlideIndex !== undefined ? { hero_slide_index: heroSlideIndex } : {}),
  });
};

/** E-3. Sent only for exposed users (both arms), so callers needn't check the arm. */
export const trackHeroMediaButtonClick = ({
  propertyId,
  button,
  count,
}: {
  propertyId: string | number;
  button: HeroMediaButton;
  count?: number;
}) => {
  const experimentProps = getOverviewGalleryEventProps();
  if (!experimentProps.gallery_variant) return;
  gtagEventEmitter({
    action: "hero_gallery_media_button_clicked",
    category: PAGE.INVENTORY,
    label: button,
    ...experimentProps,
    property_id: propertyId,
    button,
    ...(count !== undefined ? { count } : {}),
  });
};

// The arm is assigned at the CDN, not by GrowthBook, so GrowthBook's trackingCallback never fires
// for this experiment. Send the same experiment_viewed event ourselves, once per arm per page
// session as GrowthBook does, and only for exposed users (≥1200), so the GrowthBook experiment can
// still read the results from GA.
const OVERVIEW_GALLERY_EXPERIMENT_KEY = "pod_discovery_property_overview_gallery";
const exposedArms = new Set<GalleryVariant>();

export const trackOverviewGalleryExposure = (arm: GalleryVariant) => {
  if (exposedArms.has(arm)) return;
  exposedArms.add(arm);
  gtagEventEmitter({
    action: "experiment_viewed",
    category: "",
    experiment_id: OVERVIEW_GALLERY_EXPERIMENT_KEY,
    // GrowthBook's variation keys: 0 = control, 1 = variant.
    variation_id: arm === "new" ? "1" : "0",
  });
};

/**
 * Our own readout, outside GrowthBook: experiment_viewed under `overview_gallery` on every
 * property page landing, for exposed users only (same rule as getOverviewGalleryArm: desktop
 * global build at ≥1200, CDN-assigned cookie, not an internal user).
 */
export const trackOverviewGalleryLanding = (propertyId: string | number) => {
  const arm = getOverviewGalleryArm();
  if (!arm) return;
  gtagEventEmitter({
    action: "experiment_viewed",
    category: "",
    experiment_id: OVERVIEW_GALLERY_AB_TEST,
    variation_id: arm === "new" ? "1" : "0",
    property_id: propertyId,
  });
};
